/**
 * Route-Level Error Boundary
 * 
 * Catches React errors at the route level and displays a user-friendly
 * error UI with recovery options. Uses react-error-boundary for
 * production-grade error handling.
 * 
 * @module components/error/RouteErrorBoundary
 */

import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { logger } from '@/lib/logging';

const CHUNK_RELOAD_GUARD_KEY = 'route_chunk_reload_attempted';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message || '';

  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

/**
 * Fallback UI displayed when an error is caught
 */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const navigate = useNavigate();
  const chunkLoadFailure = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkLoadFailure) return;

    const reloadAttempted = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (reloadAttempted === '1') return;

    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
    window.location.reload();
  }, [chunkLoadFailure]);
  
  const handleGoHome = () => {
    window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    resetErrorBoundary();
    navigate('/');
  };

  const handleTryAgain = () => {
    if (chunkLoadFailure) {
      window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
      window.location.reload();
      return;
    }

    resetErrorBoundary();
  };

  // Extract error message safely
  const errorMessage = error instanceof Error 
    ? error.message 
    : 'An unexpected error occurred. Please try again.';
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-muted-foreground mb-6 max-w-md">
        {errorMessage}
      </p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={handleGoHome}>
          <Home className="h-4 w-4 mr-2" />
          Go Home
        </Button>
        <Button onClick={handleTryAgain}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    </div>
  );
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error boundary wrapper for route components.
 * Catches unhandled errors and displays recovery UI.
 * 
 * @example
 * <RouteErrorBoundary>
 *   <YourRouteComponent />
 * </RouteErrorBoundary>
 */
export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ErrorBoundary 
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        // In production, this would send to an error monitoring service
        // like Sentry, LogRocket, or Datadog
        logger.error('ROUTE_ERROR_BOUNDARY', { 
          error: error instanceof Error ? error.message : String(error),
          componentStack: info.componentStack 
        });
      }}
      onReset={() => {
        // Clear any cached state that might have caused the error
        // This is called when the user clicks "Try Again"
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
